import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from 'primereact/dialog';
import { Button } from '../../../components/ui/Button';
import { Checkbox } from 'primereact/checkbox';
import { Skeleton } from 'primereact/skeleton';

import { useGetAllCompanyDevices } from '../../../queries/devices';
import { useStartQuickShow } from '../../../queries/quickShow';
import { useCustomToast } from '../../Contexts/ToastContext';

/**
 * One-click "Show on Screen" wizard. Wraps three things into a single submit:
 *   1. Create a Quick Show campaign for this media file.
 *   2. Add the media to the chosen layer (default: MAIN).
 *   3. Push the campaign to N chosen screens via Firebase.
 *
 * Backend endpoint POST /api/quick-show composes the existing flows so we
 * never have to rebuild the campaign/device/firebase pipeline here.
 *
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   media: { id, title?, file_name?, thumb? }   — the media to show
 *
 * Successful submit closes the dialog and shows a toast naming the screens.
 */

// Quick Show always plays a single media item on the MAIN layer — layer
// selection (Corner/Banner/Playground overlays) lives in the full Show editor,
// not this one-click flow. Kept implicit here to keep the café flow simple.
const QUICK_SHOW_LAYER = 'MAIN';

// The fixed timed options. The "Play once (natural duration)" option is
// prepended dynamically only when the media has a known length (video /
// YouTube — duration > 0); images have no natural duration so it's hidden.
const TIMED_DURATIONS = [
  { value: null,   i18n: 'quick_show.until_stop' },
  { value: 300,    i18n: 'quick_show.minutes', vars: { n: 5 } },
  { value: 900,    i18n: 'quick_show.minutes', vars: { n: 15 } },
  { value: 1800,   i18n: 'quick_show.minutes', vars: { n: 30 } },
  { value: 3600,   i18n: 'quick_show.hour' },
];

export default function QuickShowWizard({ open, onClose, media }) {
  const { t } = useTranslation();
  const { showToast } = useCustomToast();

  const { data: devicesData, isLoading: devicesLoading } = useGetAllCompanyDevices(open);
  const devices = useMemo(() => {
    const arr = Array.isArray(devicesData?.data) ? devicesData.data
              : Array.isArray(devicesData) ? devicesData : [];
    return arr;
  }, [devicesData]);

  const [selectedIds, setSelectedIds] = useState([]);
  // `duration` holds null ("until I stop"), a number of seconds, or the
  // sentinel 'once' (Play once → natural media duration, resolved by backend).
  const [duration, setDuration]       = useState(null);

  // "Play once" only makes sense for media with a real length (video/YouTube).
  const hasNaturalDuration = Number(media?.duration) > 0;
  const durationOptions = useMemo(() => (
    hasNaturalDuration
      ? [{ value: 'once', i18n: 'quick_show.play_once' }, ...TIMED_DURATIONS]
      : TIMED_DURATIONS
  ), [hasNaturalDuration]);

  // Reset form whenever the dialog re-opens. Default to "Play once" when it's
  // available (the most natural choice for a video), else "until I stop".
  useEffect(() => {
    if (open) {
      setSelectedIds([]);
      setDuration(hasNaturalDuration ? 'once' : null);
    }
  }, [open, hasNaturalDuration]);

  const allSelected = devices.length > 0 && selectedIds.length === devices.length;
  const toggleAll = (checked) => setSelectedIds(checked ? devices.map((d) => d.id) : []);
  const toggleOne = (id, checked) => setSelectedIds((prev) =>
    checked ? [...new Set([...prev, id])] : prev.filter((x) => x !== id)
  );

  const startMut = useStartQuickShow();

  const mediaTitle = media?.title || media?.file_name || t('quick_show.this_file');

  const onSubmit = () => {
    if (selectedIds.length === 0) {
      showToast('error', t('quick_show.title'), t('quick_show.pick_at_least_one'));
      return;
    }
    const playOnce = duration === 'once';
    startMut.mutate(
      {
        media_id:         media.id,
        device_ids:       selectedIds,
        layer:            QUICK_SHOW_LAYER,
        play_once:        playOnce,
        duration_seconds: playOnce ? null : duration,
      },
      {
        onSuccess: (res) => {
          const names = (res?.data?.assigned_devices ?? [])
            .map((d) => d.name)
            .join(', ');
          showToast('success', t('quick_show.title'),
            t('quick_show.success', { file: mediaTitle, screens: names || '—' }));
          onClose?.();
        },
      },
    );
  };

  return (
    <Dialog
      header={<span><i className="pi pi-desktop me-2" />{t('quick_show.title')}</span>}
      visible={open}
      onHide={onClose}
      style={{ width: 'min(95vw, 560px)' }}
      modal
    >
      <div className="d-flex flex-column" style={{ gap: '1.1rem' }}>
        {/* File preview row */}
        <div className="d-flex align-items-center gap-3 p-3"
             style={{ background: 'var(--pp-bg-secondary)', borderRadius: 'var(--pp-radius-md)' }}>
          <div style={{
            width: 64, height: 64, borderRadius: 'var(--pp-radius-md)',
            background: 'var(--pp-bg-tertiary)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            overflow: 'hidden', flexShrink: 0,
          }}>
            {media?.thumb
              ? <img src={media.thumb} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                     onError={(e) => { e.currentTarget.style.display = 'none'; }} />
              : <i className="pi pi-image" style={{ fontSize: '1.6rem', color: 'var(--pp-text-secondary)' }} />}
          </div>
          <div className="min-w-0 flex-grow-1">
            <div className="small text-muted">{t('quick_show.file')}</div>
            <div className="text-truncate fw-semibold" style={{ color: 'var(--pp-text-primary)' }}>
              {mediaTitle}
            </div>
          </div>
        </div>

        {/* Screens */}
        <div className="pp-field">
          <label className="pp-field-label">
            <i className="pi pi-desktop" />
            {t('quick_show.which_screens')}
            <span className="pp-field-required">*</span>
          </label>
          {devicesLoading ? (
            <>
              <Skeleton height="2.2rem" className="mb-2" />
              <Skeleton height="2.2rem" className="mb-2" />
              <Skeleton height="2.2rem" />
            </>
          ) : devices.length === 0 ? (
            <p className="pp-field-error">
              <i className="pi pi-exclamation-circle" />
              {t('quick_show.no_screens')}
            </p>
          ) : (
            <div className="d-flex flex-column gap-2"
                 style={{ maxHeight: '30vh', overflowY: 'auto' }}>
              {devices.map((d) => (
                <label key={d.id} className="d-flex align-items-center gap-2 p-2 rounded"
                       style={{
                         background: selectedIds.includes(d.id) ? 'color-mix(in srgb, var(--pp-btn-honey) 10%, transparent)' : 'transparent',
                         border: selectedIds.includes(d.id) ? '1px solid var(--pp-btn-honey)' : '1px solid var(--pp-border)',
                         cursor: 'pointer',
                       }}>
                  <Checkbox
                    checked={selectedIds.includes(d.id)}
                    onChange={(e) => toggleOne(d.id, e.checked)}
                  />
                  <span className="fw-semibold">{d.name || d.serial_name}</span>
                  {d.name && <span className="small text-muted">— {d.serial_name}</span>}
                </label>
              ))}
              <div style={{ borderTop: '1px solid var(--pp-border)', margin: '0.25rem 0' }} />
              <label className="d-flex align-items-center gap-2 p-2 rounded fw-semibold"
                     style={{ cursor: 'pointer', color: 'var(--pp-btn-honey)' }}>
                <Checkbox checked={allSelected} onChange={(e) => toggleAll(e.checked)} />
                {t('quick_show.all_screens')}
              </label>
            </div>
          )}
        </div>

        {/* Duration */}
        <div className="pp-field">
          <label className="pp-field-label">
            <i className="pi pi-clock" />
            {t('quick_show.duration')}
          </label>
          <div className="d-flex flex-wrap gap-2">
            {durationOptions.map((d) => {
              const isActive = duration === d.value;
              return (
                <button
                  key={d.i18n + (d.vars?.n ?? '')}
                  type="button"
                  className={`pp-chip ${isActive ? 'pp-chip-active' : ''}`}
                  onClick={() => setDuration(d.value)}
                  style={{ minHeight: 36 }}
                >
                  {t(d.i18n, d.vars)}
                </button>
              );
            })}
          </div>
        </div>

        <div className="pp-dialog-footer">
          <Button label={t('buttons.cancel')} severity="secondary" outlined onClick={onClose} />
          <Button
            icon="pi pi-play"
            label={t('quick_show.show_now')}
            loading={startMut.isLoading}
            disabled={devices.length === 0 || selectedIds.length === 0}
            onClick={onSubmit}
          />
        </div>
      </div>
    </Dialog>
  );
}
